import { gatewayApiAgent } from 'common/api/apiAgents';
import { CountryCode } from 'common/api-types/common';
import {
  DelistingRecommendationsApproveRequest,
  DelistingRecommendationsBulkApproveRequest,
  DelistingRecommendationsBulkRejectRequest,
  DelistingRecommendationsListRequest,
  DelistingRecommendationsRejectRequest,
  DelistingRecommendationsResponse,
  ListingRecommendationsApproveRequest,
  ListingRecommendationsBulkApproveRequest,
  ListingRecommendationsBulkRejectRequest,
  ListingRecommendationsListRequest,
  ListingRecommendationsRejectRequest,
  ListingRecommendationsResponse,
  PerformanceOverviewRequest,
  PerformanceOverviewResponse,
  PromotionRecommendationsApproveRequest,
  PromotionRecommendationsBulkApproveRequest,
  PromotionRecommendationsBulkRejectRequest,
  PromotionRecommendationsListRequest,
  PromotionRecommendationsRejectRequest,
  PromotionRecommendationsResponse,
  RecommendationPreferencesRequest,
  RecommendationPreferencesResponse,
} from 'common/api-types/recommendations';

export const fetchPerformanceOverviewPreferences = async (countryCode: CountryCode) => {
  const res = await gatewayApiAgent.get<RecommendationPreferencesResponse>(
    `selection-intelligence/public/v1/selection-intelligence/${countryCode}/performance-overview/preferences`,
  );

  return res.data;
};

export const fetchPerformanceOverview = async (
  countryCode: CountryCode,
  request: PerformanceOverviewRequest,
) => {
  const res = await gatewayApiAgent.post<PerformanceOverviewResponse>(
    `selection-intelligence/public/v1/selection-intelligence/${countryCode}/performance-overview`,
    request,
  );

  return res.data;
};

export const fetchListingRecommendations = async (
  countryCode: CountryCode,
  request: ListingRecommendationsListRequest,
) => {
  const res = await gatewayApiAgent.post<ListingRecommendationsResponse>(
    `selection-intelligence/public/v1/selection-intelligence/${countryCode}/listing-recommendations/list`,
    request,
  );

  return res.data;
};

export const fetchPromotionRecommendations = async (
  countryCode: CountryCode,
  request: PromotionRecommendationsListRequest,
) => {
  const res = await gatewayApiAgent.post<PromotionRecommendationsResponse>(
    `selection-intelligence/public/v1/selection-intelligence/${countryCode}/promotion-recommendations/list`,
    request,
  );

  return res.data;
};

export const fetchDelistingRecommendations = async (
  countryCode: CountryCode,
  request: DelistingRecommendationsListRequest,
) => {
  const res = await gatewayApiAgent.post<DelistingRecommendationsResponse>(
    `selection-intelligence/public/v1/selection-intelligence/${countryCode}/delisting-recommendations/list`,
    request,
  );

  return res.data;
};

export const updatePerformanceOverviewPreferences = async (
  countryCode: CountryCode,
  request: RecommendationPreferencesRequest,
) => {
  const res = await gatewayApiAgent.post<RecommendationPreferencesResponse>(
    `selection-intelligence/public/v1/selection-intelligence/${countryCode}/performance-overview/preferences`,
    request,
  );

  return res.data;
};

// Promotion Recommendations
export const approvePromotionRecommendations = async (
  request: PromotionRecommendationsApproveRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `selection-intelligence/public/v1/selection-intelligence/promotion-recommendations/approve`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const bulkApprovePromotionRecommendations = async (
  request: PromotionRecommendationsBulkApproveRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `selection-intelligence/public/v1/selection-intelligence/promotion-recommendations/approve/bulk`,
    request,
  );

  return res.data;
};

export const rejectPromotionRecommendations = async (
  request: PromotionRecommendationsRejectRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/promotion-recommendations/reject`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const bulkRejectPromotionRecommendations = async (
  request: PromotionRecommendationsBulkRejectRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/promotion-recommendations/reject/bulk`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

// Delisting Recommendations
export const approveDelistingRecommendations = async (
  request: DelistingRecommendationsApproveRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/delisting-recommendations/approve`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const bulkApproveDelistingRecommendations = async (
  request: DelistingRecommendationsBulkApproveRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/delisting-recommendations/approve/bulk`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const rejectDelistingRecommendations = async (
  request: DelistingRecommendationsRejectRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/delisting-recommendations/reject`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const bulkRejectDelistingRecommendations = async (
  request: DelistingRecommendationsBulkRejectRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/delisting-recommendations/reject/bulk`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

// Listing Recommendations
export const approveListingRecommendations = async (
  request: ListingRecommendationsApproveRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/listing-recommendations/approve`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const bulkApproveListingRecommendations = async (
  request: ListingRecommendationsBulkApproveRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/listing-recommendations/approve/bulk`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const rejectListingRecommendations = async (
  request: ListingRecommendationsRejectRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/listing-recommendations/reject`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};

export const bulkRejectListingRecommendations = async (
  request: ListingRecommendationsBulkRejectRequest,
) => {
  const res = await gatewayApiAgent.post<Blob>(
    `/selection-intelligence/public/v1/selection-intelligence/listing-recommendations/reject/bulk`,
    request,
    {
      responseType: 'blob',
    },
  );

  return res.data;
};
